import { CloudOff, RefreshCw } from 'lucide-react';
import { formatDate } from '@/shared/domain/format';
import { useT } from '@/shared/i18n/useT';
import { useAppSelector } from '@/shared/store/hooks';
import { Icon } from '../icons/Icon';
import { Button } from '../ui/Button';
import { feedbackMsgs } from './feedbackMsgs';

export interface ConnectionBannerProps {
  /** Fecha del último dato guardado ('YYYY-MM-DD'), si hay copia local. */
  snapshotDate?: string | null;
  /** Reintentar la comprobación/carga. Si falta, no se muestra el botón. */
  onRetry?: () => void;
}

/**
 * Franja bajo la barra superior cuando no hay conexión (`ui.offline`) o la API no responde
 * (`ui.apiReachable === false`). Dice qué pasa y de cuándo es lo que se está viendo.
 */
export function ConnectionBanner({ snapshotDate, onRetry }: ConnectionBannerProps) {
  const { t, lang } = useT(feedbackMsgs);
  const offline = useAppSelector((s) => s.ui.offline);
  const apiReachable = useAppSelector((s) => s.ui.apiReachable);
  if (!offline && apiReachable) return null;

  const date = snapshotDate ? formatDate(snapshotDate, 'media', lang) : null;
  const key = offline
    ? date
      ? 'offline.withDate'
      : 'offline.noDate'
    : date
      ? 'offline.apiDownWithDate'
      : 'offline.apiDown';

  return (
    <div role="status" className="border-b border-warn/30 bg-warn-tint">
      <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-2 text-sm text-fg md:px-6">
        <Icon icon={CloudOff} size={16} className="shrink-0 text-warn" />
        <p className="min-w-0 flex-1">{t(key, { date: date ?? '' })}</p>
        {onRetry ? (
          <Button variant="ghost" size="sm" onClick={onRetry} className="shrink-0">
            <Icon icon={RefreshCw} size={16} />
            {t('offline.refresh')}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
